import { useColorScheme, useLocales } from "@/lib/hooks";
import { AsideMenuItemLinkType, BgColorType, Icon } from "@/lib/main";
import { CSSProperties, useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router";

type PropsType = {
  asideMenuItems: AsideMenuItemLinkType[];
  placeholder?: string;
  bgColor?: BgColorType;
};

export default function AsideNavSelect({
  asideMenuItems,
  placeholder = "Select...",
  bgColor,
}: PropsType) {
  const l = useLocales();
  const location = useLocation();
  const { colorScheme } = useColorScheme();
  const [showOptions, setShowOptions] = useState(false);
  const [openHeight, setOpenHeight] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);

  const currentPath = location.pathname + location.hash;
  const activeItem = asideMenuItems.find((item) => item.to === currentPath);
  const displayText = activeItem ? l(activeItem.title) : placeholder;

  useEffect(() => {
    if (!showOptions) return;
    const handleOutsideClick = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setShowOptions(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [showOptions]);

  const handleTriggerClick = () => {
    if (!showOptions && wrapperRef.current && optionsRef.current) {
      const wrapperBottom = wrapperRef.current.getBoundingClientRect().bottom;
      const footer = document.querySelector("footer");
      const footerTop = footer
        ? footer.getBoundingClientRect().top
        : window.innerHeight;
      const available = Math.min(216, footerTop - wrapperBottom - 8);
      setOpenHeight(Math.min(optionsRef.current.scrollHeight, available));
    }
    setShowOptions((prev) => !prev);
  };

  const wrapperStyles: CSSProperties = {
    position: "relative",
    width: "100%",
  };

  const triggerStyles: CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "var(--spacing-sm)",

    height: "3rem",
    padding: "0 var(--spacing-md)",

    backgroundColor: activeItem
      ? "var(--color-background-brand)"
      : bgColor
        ? `var(--color-background-${bgColor})`
        : colorScheme === "light"
          ? "var(--color-background-elevated)"
          : "var(--color-background-frame)",

    borderRadius: showOptions
      ? "var(--border-radius-sm) var(--border-radius-sm) 0 0"
      : "var(--border-radius-sm)",

    color: activeItem
      ? colorScheme === "light"
        ? "var(--color-text-always-light)"
        : "var(--color-text-always-dark)"
      : "var(--color-text-primary)",

    fontSize:
      "clamp(var(--font-size-xs), 0.5rem + 0.75vw, var(--font-size-navbar-item))",
    fontWeight: activeItem ? 600 : 400,

    cursor: "pointer",
    userSelect: "none",
    overflow: "hidden",
  };

  const triggerTextStyles: CSSProperties = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
  };

  // Outer clip: animates between 0 and the measured pixel height
  const optionsWrapperStyles: CSSProperties = {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    zIndex: 1000,
    height: showOptions ? `${openHeight}px` : "0px",
    overflow: "hidden",
    transition: "height 0.5s ease, opacity 0.5s ease",
    opacity: showOptions ? 1 : 0,
    borderRadius: "0 0 var(--border-radius-sm) var(--border-radius-sm)",
  };

  // Inner scroll container: capped at openHeight, scrollable if content overflows
  const optionsInnerStyles: CSSProperties = {
    maxHeight: `${openHeight}px`,
    overflowY: "auto",
    backgroundColor: "var(--color-background-canvas)",
    border: "var(--border-width-sm) solid var(--color-border-quaternary)",
    borderTop: "none",
    borderRadius: "0 0 var(--border-radius-sm) var(--border-radius-sm)",
  };

  const getNavLinkStyles = (isActive: boolean): CSSProperties => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",

    width: "100%",
    height: "3rem",
    padding: "0 var(--spacing-md)",

    backgroundColor: isActive
      ? "var(--color-background-frame)"
      : "var(--color-background-canvas)",
    color: "var(--color-text-primary)",
    fontSize: "var(--font-size-xs)",
    fontWeight: isActive ? 600 : 400,
    textDecoration: "none",
    cursor: "pointer",

    borderBottom: `var(--border-width-xs) solid var(--color-border-subtle)`,

    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  });

  const chevronColor = activeItem
    ? colorScheme === "light"
      ? "always-light"
      : "always-dark"
    : "secondary";

  return (
    <div ref={wrapperRef} style={wrapperStyles}>
      <div role="button" style={triggerStyles} onClick={handleTriggerClick}>
        <span style={triggerTextStyles}>{displayText}</span>
        <Icon
          name={showOptions ? "chevron-up" : "chevron-down"}
          color={chevronColor}
        />
      </div>

      <div style={optionsWrapperStyles}>
        <div ref={optionsRef} style={optionsInnerStyles}>
          {asideMenuItems.map((item) => {
            const isActive = item.to === currentPath;
            return (
              <NavLink
                key={item.id}
                to={item.to}
                style={getNavLinkStyles(isActive)}
                onClick={() => setShowOptions(false)}
              >
                {l(item.title)}
                {isActive && <Icon name="check" color="primary" size="md" />}
              </NavLink>
            );
          })}
        </div>
      </div>
    </div>
  );
}
